import type { DateTime } from 'luxon'

import type { FormattedRow } from '@/features/bessDashboard/types'
import { filterByMarketProgram, filterByType } from '@/features/bessDashboard/utils/chart'
import type { DailyForecastPrices, HourlyQuantilePrices } from '@/features/bidding/types/forecastPricing'

import { convertForecastsToMarketTimezone, getDayHoursInTimezone } from './convertForecastTimezone'

const PRICE_FORECAST_TYPE = 'capacity_price_per_MW_h_HOURLY'

/**
 * Extract hourly quantile prices for a specific market and date.
 *
 * @param forecastsData - Raw forecast data from Databricks API
 * @param marketProgramName - Market program name (e.g., 'mFRR UP', 'mFRR DOWN')
 * @param selectedDate - The delivery date to extract prices for
 * @param marketTimezone - IANA timezone string (e.g., 'Europe/Paris')
 * @returns DailyForecastPrices or null if no data available
 */
export function extractHourlyQuantilePrices(
  forecastsData: FormattedRow[],
  marketProgramName: string,
  selectedDate: DateTime,
  marketTimezone: string,
): DailyForecastPrices | null {
  // Filter by market program
  const marketData = filterByMarketProgram(forecastsData, marketProgramName)
  if (marketData.length === 0) {
    return null
  }

  // Filter by price forecast type
  const priceData = filterByType(marketData, PRICE_FORECAST_TYPE)
  if (priceData.length === 0) {
    return null
  }

  // Convert to market timezone
  const processedRows = convertForecastsToMarketTimezone(priceData, marketTimezone)
  if (processedRows.length === 0) {
    return null
  }

  // Filter by selected date
  const targetDateStr = selectedDate.setZone(marketTimezone).toISODate()
  if (targetDateStr === null) {
    return null
  }

  const dayRows = processedRows.filter((row) => row.date === targetDateStr)
  if (dayRows.length === 0) {
    return null
  }

  // Build hourly prices map
  const hourlyPrices = new Map<number, HourlyQuantilePrices>()

  for (const row of dayRows) {
    // If we have duplicate hours (shouldn't happen), take the first one
    if (!hourlyPrices.has(row.hour)) {
      hourlyPrices.set(row.hour, {
        hour: row.hour,
        p10: row.p10,
        p25: row.p25,
        mean: row.mean,
        p75: row.p75,
        p90: row.p90,
      })
    }
  }

  const dayHours = getDayHoursInTimezone(selectedDate, marketTimezone)

  return {
    date: targetDateStr,
    marketProgramName,
    hourlyPrices,
    dayHours,
  }
}

/**
 * Check if forecast data is complete for a day.
 * A complete day has data for all expected hours (23, 24, or 25 depending on DST).
 *
 * @param dailyPrices - The daily forecast prices to check
 * @returns true if all hours have data
 */
export function isForecastDataComplete(dailyPrices: DailyForecastPrices): boolean {
  return dailyPrices.hourlyPrices.size === dailyPrices.dayHours
}

/**
 * Get missing hours from forecast data.
 *
 * @param dailyPrices - The daily forecast prices to check
 * @returns Array of missing hours (0-23)
 */
export function getMissingForecastHours(dailyPrices: DailyForecastPrices): number[] {
  const missing: number[] = []
  for (let hour = 0; hour < dailyPrices.dayHours; hour++) {
    if (!dailyPrices.hourlyPrices.has(hour)) {
      missing.push(hour)
    }
  }
  return missing
}

/**
 * Validation result for daily forecast prices.
 */
export type ForecastValidationResult = {
  isComplete: boolean
  missingHours: number[]
  availableHours: number
  expectedHours: number
}

/**
 * Validate forecast data completeness.
 * Returns a validation result object with completeness status and details.
 *
 * @param dailyPrices - The daily forecast prices to validate
 * @returns Validation result with completeness status
 */
export function validateForecastData(dailyPrices: DailyForecastPrices): ForecastValidationResult {
  const missingHours = getMissingForecastHours(dailyPrices)
  return {
    isComplete: missingHours.length === 0,
    missingHours,
    availableHours: dailyPrices.hourlyPrices.size,
    expectedHours: dailyPrices.dayHours,
  }
}
